/**
 * AsyncRef is a utility class for managing asynchronous states in Vue.js.
 * It represents the state of an asynchronous operation (e.g., an API call),
 * providing a simple way to track whether the operation is in progress, the resulting data, or any errors.
 *
 * Typical usage:
 * - `loading`: true while the operation is ongoing, false when completed.
 * - `data`: contains the data returned by the operation if successful.
 * - `error`: contains the error if the operation fails.
 *
 * Example:
 * const myAsyncRef = new asyncRef(true); // Starts with loading true
 * // After loading...
 * myAsyncRef.loading = false;
 * myAsyncRef.data = result;
 * // Or in case of error:
 * myAsyncRef.error = error;
 */
class asyncRef {
  /**
   * Indicates whether the asynchronous operation is currently in progress.
   * @type {boolean}
   */
  loading = false;

  /**
   * Contains the data returned by the operation if successful.
   * @type {any}
   */
  data = null;

  /**
   * Contains the error if the operation fails.
   * @type {any}
   */
  error = null;

  /**
   * Constructor to initialize the AsyncRef state.
   * @param {boolean} [initialLoading=false] - Initial loading state.
   * @param {any} [initialData=null] - Initial data.
   * @param {any} [initialError=null] - Initial error.
   */
  constructor(initialLoading = false, initialData = null, initialError = null) {
    this.loading = initialLoading;
    this.data = initialData;
    this.error = initialError;
  }
}

export default asyncRef;